/**
 * CustomerDetail — the detail pane for the selected pickup stop.
 * Replaces the col-40 <div class="card"> section in templates/dashboard.html.
 *
 * One arrangement in every Size class, from top to bottom:
 *   Header row   the order number and address, the pickup code and customer name, and
 *                the Directions, My Notes, Customer info and More buttons. More holds
 *                the Pickup Code and Move to Order # Selects.
 *   Info block   read-only, and the only part that scrolls.
 *   Stop strip   the bag stepper and Complete (or Mark Pending), pinned to the bottom so
 *                the Stop actions stay on screen however long the notes are.
 *
 * Props mirror the $scope fields the original template bound to:
 *   current, bagOptions, datalists, onToggle, onBagsChange, onOrderChange,
 *   onEditNotes, onShowMap, onShowCustomerInfo.
 */

import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Combobox,
  Group,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Text,
  useCombobox,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconCheck,
  IconDots,
  IconInfoCircle,
  IconMap,
  IconMinus,
  IconNote,
  IconPlus,
} from '@tabler/icons-react';

import { stepBags } from './bagStepper';
import { openInWaze } from './waze';
import type { BagOption, DataLists, RunsheetLine } from '@/types';

// Three options high, so the option list of a Select in the More dropdown fits above or
// below its input in a phone's landscape browser tab, which is 334px high.
const OPTION_LIST_HEIGHT = 150;

// A Button label is one line. The bag count and Mark Pending need two in a narrow pane.
const TWO_LINE_LABEL = { label: { whiteSpace: 'normal', lineHeight: 1.15 } } as const;

function formatPhone(raw: string): string {
  if (!raw || typeof raw !== 'string') return String(raw || '');
  let d = raw.replace(/\D/g, '');
  // Strip leading 0 from local landline numbers (e.g. 033902929 → 33902929)
  if (d.length === 9 && d.startsWith('0')) d = d.slice(1);
  if (d.length === 8) return `${d.slice(0, 4)} ${d.slice(4)}`;
  if (d.length === 10 && (d.startsWith('04') || d.startsWith('05')))
    return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
  return raw;
}

function nl2br(text: string): React.ReactNode {
  return text.split('\n').map((line, i) => (
    <span key={i}>
      {line}
      <br />
    </span>
  ));
}

interface Props {
  current: RunsheetLine;
  bagOptions: BagOption[];
  datalists: DataLists;
  disableCountdown: number;
  onToggle: (status: boolean) => void;
  onBagsChange: (val: number) => void;
  onOrderChange: (displayorder: number) => void;
  onPickupCodeChange: (code: string) => void;
  onEditNotes: () => void;
  onShowCustomerInfo: () => void;
  orderOptions: string[];
}

export function CustomerDetail({
  current,
  bagOptions,
  datalists,
  disableCountdown,
  onToggle,
  onBagsChange,
  onOrderChange,
  onPickupCodeChange,
  onEditNotes,
  onShowCustomerInfo,
  orderOptions,
}: Props) {
  const [moreOpen, setMoreOpen] = useState(false);

  const pickupCodes: { value: string; label: string }[] = Array.from(
    new Map(
      (datalists['driver-pickup-code'] ?? []).map(item => [
        item.name,
        { value: item.name, label: item.name },
      ]),
    ).values(),
  );

  const isDisabled =
    disableCountdown > 0 ||
    current.num_bags <= 0 ||
    current.pickupcode.match(/overdue|cancelled/i) !== null;

  const serviceFreq =
    {
      1: 'Monthly',
      2: 'Bi-Monthly (Odd)',
      3: 'Bi-Monthly (Even)',
      5: 'Quarterly',
    }[current.which] ?? '';

  const fewerBags = stepBags(current.num_bags, 'down', bagOptions);
  const moreBags = stepBags(current.num_bags, 'up', bagOptions);

  return (
    <Box h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* ── Header row ── */}
      <Group
        gap="xs"
        p="xs"
        wrap="nowrap"
        style={{ flexShrink: 0, borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} truncate>
            {current.displayorder > 0 && `#${current.displayorder} `}
            {current.deliveryaddress}
          </Text>
          <Text fz="sm" c="dimmed" truncate>
            {current.pickupcode} · {current.namewithid}
          </Text>
        </Box>
        <ActionIcon color="blue" aria-label="Directions" onClick={() => openInWaze(current)}>
          <IconMap size={20} />
        </ActionIcon>
        <ActionIcon
          color={current.driver_notes ? 'red' : 'blue'}
          aria-label="My Notes"
          onClick={onEditNotes}
        >
          <IconNote size={20} />
        </ActionIcon>
        <ActionIcon variant="light" aria-label="Customer info" onClick={onShowCustomerInfo}>
          <IconInfoCircle size={20} />
        </ActionIcon>
        {/* The dropdown opens beside the More button and level with it. At Short height
            there is no room for it, or for the Selects' option lists, under the header row. */}
        <Popover opened={moreOpen} onChange={setMoreOpen} position="left-start" width={260}>
          <Popover.Target>
            <ActionIcon variant="default" aria-label="More" onClick={() => setMoreOpen(o => !o)}>
              <IconDots size={20} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            {/* The option lists render inside the dropdown. In a portal, a tap on an
                option would count as a tap outside and close the dropdown first. */}
            <Stack gap="xs">
              <Select
                label="Pickup Code"
                data={pickupCodes}
                value={current.pickupcode}
                onChange={val => {
                  if (!val) return;
                  onPickupCodeChange(val);
                  setMoreOpen(false);
                }}
                maxDropdownHeight={OPTION_LIST_HEIGHT}
                comboboxProps={{ withinPortal: false }}
              />
              <Select
                label="Move to Order #"
                data={orderOptions}
                value={current.displayorder > 0 ? String(current.displayorder) : null}
                onChange={val => {
                  if (!val) return;
                  onOrderChange(Number(val));
                  setMoreOpen(false);
                }}
                maxDropdownHeight={OPTION_LIST_HEIGHT}
                comboboxProps={{ withinPortal: false }}
              />
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>

      {/* ── Info block (read-only, scrolls) ── */}
      <ScrollArea flex={1} p="xs" style={{ minHeight: 0 }}>
        <Stack gap={4}>
          <InfoRow label="Service">
            {current.num_bags} bags @ ${current.cost} ea —{' '}
            {current.invnum === 0
              ? 'PAYG'
              : current.invnum > 0
                ? `${current.invnum} bags in advance`
                : 'On Account'}{' '}
            on a {serviceFreq} Service
          </InfoRow>

          {current.pension && <InfoRow label="Pensioner">YES</InfoRow>}

          <InfoRow label="Customer">{current.namewithid}</InfoRow>

          {current.ph_home && <InfoRow label="Phone (H)">{formatPhone(current.ph_home)}</InfoRow>}
          {current.ph_work && <InfoRow label="Phone (W)">{formatPhone(current.ph_work)}</InfoRow>}
          {current.ph_mob && <InfoRow label="Phone (Mob)">{formatPhone(current.ph_mob)}</InfoRow>}

          {current.driver_notes && (
            <InfoRow label="Driver Notes" strong>
              {nl2br(current.driver_notes)}
            </InfoRow>
          )}
          {current.cont_notes && (
            <InfoRow label="Office Notes" strong>
              {nl2br(current.cont_notes)}
            </InfoRow>
          )}
          {current.cust_notes && (
            <InfoRow label="Notes" strong>
              {nl2br(current.cust_notes)}
            </InfoRow>
          )}
          {current.envelope && (
            // A Badge's own text is 11px to 13px, under the info block's 14px.
            <Badge color="orange" size="lg" fz="sm" mt={4}>
              Cash Envelope Customer
            </Badge>
          )}
        </Stack>
      </ScrollArea>

      {/* ── Stop strip (pinned) ── */}
      <Group
        data-testid="stop-strip"
        gap="xs"
        p="xs"
        wrap="nowrap"
        style={{ flexShrink: 0, borderTop: '1px solid var(--mantine-color-gray-3)' }}
      >
        {/* The stepper stays enabled once the stop is completed, as the bag Select did. */}
        <ActionIcon
          size="input-lg"
          variant="default"
          aria-label="One fewer bag"
          disabled={fewerBags === current.num_bags}
          onClick={() => onBagsChange(fewerBags)}
        >
          <IconMinus size={20} />
        </ActionIcon>
        <BagCount count={current.num_bags} bagOptions={bagOptions} onChange={onBagsChange} />
        <ActionIcon
          size="input-lg"
          variant="default"
          aria-label="One more bag"
          disabled={moreBags === current.num_bags}
          onClick={() => onBagsChange(moreBags)}
        >
          <IconPlus size={20} />
        </ActionIcon>

        {!current.driver_locked ? (
          <Button
            color="green"
            px="xs"
            leftSection={<IconCheck size={20} />}
            onClick={() => onToggle(true)}
            disabled={isDisabled}
            style={{ flex: 1, minWidth: 0 }}
          >
            {disableCountdown > 0 ? `Wait (${disableCountdown}s)` : 'Complete'}
          </Button>
        ) : (
          <Button
            color="red"
            px="xs"
            leftSection={<IconArrowBackUp size={20} />}
            onClick={() => onToggle(false)}
            styles={TWO_LINE_LABEL}
            style={{ flex: 1, minWidth: 0 }}
          >
            Mark Pending
          </Button>
        )}
      </Group>
    </Box>
  );
}

// ── Small sub-components ──────────────────────────────────────────────────────

/**
 * The stepper's count. It is also the way to the full bag list, for half bags and big
 * jumps, so it is a button that opens the list above the Stop strip.
 */
function BagCount({
  count,
  bagOptions,
  onChange,
}: {
  count: number;
  bagOptions: BagOption[];
  onChange: (val: number) => void;
}) {
  const combobox = useCombobox({
    // The list has about 200 options, so open it at the current count.
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active', { scrollIntoView: true }),
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const label = bagOptions.find(o => Number(o.id) === count)?.label ?? String(count);

  return (
    <Combobox
      store={combobox}
      size="lg"
      position="top-start"
      width={200}
      onOptionSubmit={val => {
        onChange(Number(val));
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target targetType="button">
        <Button
          variant="default"
          w={72}
          px={4}
          fz="md"
          aria-label={`Bags picked up: ${label}`}
          onClick={() => combobox.toggleDropdown()}
          styles={TWO_LINE_LABEL}
          style={{ flexShrink: 0 }}
        >
          {label}
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={220} type="scroll">
            {bagOptions.map(o => {
              const current = Number(o.id) === count;
              return (
                <Combobox.Option
                  key={o.id}
                  value={String(o.id)}
                  active={current}
                  bg={current ? 'blue.0' : undefined}
                  fw={current ? 700 : undefined}
                >
                  {o.label}
                </Combobox.Option>
              );
            })}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

function InfoRow({
  label,
  strong = false,
  children,
}: {
  label: string;
  strong?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Text component="span" fz="sm" c="dimmed" w={96} style={{ flexShrink: 0 }}>
        {label}:
      </Text>
      <Text component="span" fz="sm" fw={strong ? 700 : undefined} style={{ flex: 1 }}>
        {children}
      </Text>
    </Group>
  );
}
